// Laravel adapter for the vendored @edm/web editor. Replaces the original
// envelope-based client that targeted the @edm/server API. Talks to the
// Dashboard Online endpoints under /api/v1/emails/*, translating the editor's
// EmailDocument OBJECT <-> Laravel's opaque document_json STRING, mapping URLs,
// and providing identity from window.__EMAIL_EDITOR__ (injected by the Blade
// view) so the editor never shows a login screen.
import type { EmailDocument } from "@edm/schema";

export interface ApiError {
    code: string;
    message: string;
    details?: unknown;
}

export class ApiCallError extends Error {
    readonly code: string;
    readonly status: number;
    readonly details?: unknown;
    constructor(status: number, err: ApiError) {
        super(err.message);
        this.name = "ApiCallError";
        this.status = status;
        this.code = err.code;
        this.details = err.details;
    }
}

interface EditorConfig {
    templateId: string;
    csrf: string;
    capabilities: { send_test: boolean; ai: boolean; eden: boolean };
    me: MeResult;
}

function cfg(): EditorConfig {
    const w = (window as unknown as { __EMAIL_EDITOR__?: EditorConfig }).__EMAIL_EDITOR__;
    return (
        w ?? {
            templateId: "",
            csrf: "",
            capabilities: { send_test: false, ai: false, eden: false },
            me: {
                user_id: "",
                account_id: "",
                roles: [],
                capabilities: { send_test: false, ai: false, eden: false },
            },
        }
    );
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
    onUnauthorized = fn;
}

async function raw<T>(method: string, url: string, body?: unknown): Promise<T> {
    const res = await fetch(url, {
        method,
        credentials: "include",
        headers: {
            "content-type": "application/json",
            "X-CSRF-TOKEN": cfg().csrf,
            accept: "application/json",
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    let data: unknown = null;
    try {
        data = await res.json();
    } catch {
        data = null;
    }
    if (!res.ok) {
        if (res.status === 401 && onUnauthorized) onUnauthorized();
        const err = (data ?? {}) as { error?: string; code?: string; message?: string; details?: unknown };
        throw new ApiCallError(res.status, {
            code: err.code ?? (res.status === 409 ? "VERSION_CONFLICT" : "HTTP_" + res.status),
            message: err.error ?? err.message ?? `HTTP ${res.status}`,
            details: err.details,
        });
    }
    return data as T;
}

// Map the editor's expected /api/v1/* URLs onto the Laravel email API.
function mapGet(url: string): string {
    if (url.startsWith("/api/v1/emails")) return url;
    return url.replace("/api/v1/", "/api/v1/emails/");
}

export const api = {
    get: async <T>(url: string): Promise<T> => {
        if (url === "/api/v1/me") return cfg().me as unknown as T;
        if (url === "/api/v1/auth/mode") return { mode: "mock" } as unknown as T;
        // The editor expects a bare array here; Laravel wraps them — unwrap so
        // `.map(...)` in the palette/inspector doesn't crash the render.
        if (url === "/api/v1/brand-kits") {
            const r = await raw<{ brandKits?: unknown[] }>("GET", "/api/v1/emails/brand-kits");
            return (Array.isArray(r?.brandKits) ? r.brandKits : []) as unknown as T;
        }
        if (url === "/api/v1/blocks") {
            const r = await raw<{ savedBlocks?: unknown[] }>("GET", "/api/v1/emails/saved-blocks");
            return (Array.isArray(r?.savedBlocks) ? r.savedBlocks : []) as unknown as T;
        }
        return raw<T>("GET", mapGet(url));
    },
    post: async <T>(url: string, body?: unknown): Promise<T> => {
        if (url === "/api/v1/render") {
            const b = (body ?? {}) as { document?: unknown; sampleData?: unknown; brandTokens?: unknown };
            return raw<T>("POST", "/api/v1/emails/render", {
                document_json: JSON.stringify(b.document ?? {}),
                sampleData: b.sampleData,
                brandTokens: b.brandTokens,
            });
        }
        if (url === "/api/v1/ai/assist") {
            const b = (body ?? {}) as { action?: string };
            return raw<T>("POST", "/api/v1/emails/ai/" + (b.action ?? "rewrite"), body);
        }
        if (url === "/api/v1/ai/feedback") return { ok: true } as unknown as T;
        return raw<T>("POST", mapGet(url), body);
    },
};

// ---- typed endpoints (shapes the editor imports) ---------------------------

export interface EmailSummary {
    id: string;
    title: string;
    subject: string;
    status: string;
    version: number;
    updatedAt: string;
    updatedBy: string;
    createdAt: string;
}
export interface EmailDetail extends EmailSummary {
    preheader: string;
    document: EmailDocument;
}
export interface RenderWarning {
    ruleId: string;
    severity: "error" | "warning" | "info";
    nodeId: string | null;
    message: string;
}
export interface RenderResult {
    html: string;
    text: string;
    subject: string;
    preheader: string;
    warnings: RenderWarning[];
    sizeBytes: number;
}
export interface Account {
    id: string;
    name: string;
    slug: string;
}
export interface MeResult {
    user_id: string;
    account_id: string;
    roles: string[];
    capabilities: { send_test: boolean; ai: boolean; eden: boolean };
}
export interface LoginResult {
    user: { id: string; email: string; name: string };
    account_id: string;
    accounts: Account[];
}

export const authApi = {
    mode: () => Promise.resolve({ mode: "mock" as const }),
    me: () => Promise.resolve(cfg().me),
    accounts: () => Promise.resolve({ accounts: [] as Account[] }),
    login: () =>
        Promise.reject(new ApiCallError(400, { code: "UNSUPPORTED", message: "Embedded editor uses the host session" })),
    selectAccount: (accountId: string) => Promise.resolve({ account_id: accountId }),
    logout: () => Promise.resolve({ loggedOut: true }),
};

const nowIso = () => new Date().toISOString();

interface DocumentResponse {
    id: string | number;
    tid: string;
    title: string;
    engine: string;
    document_json: string;
}

function toDetail(r: DocumentResponse): EmailDetail {
    const doc = JSON.parse(r.document_json) as EmailDocument;
    return {
        id: String(r.id),
        title: r.title ?? doc.meta?.title ?? "Email",
        subject: doc.meta?.subject ?? "",
        preheader: doc.meta?.preheader ?? "",
        status: "draft",
        version: 1,
        updatedAt: nowIso(),
        updatedBy: "",
        createdAt: nowIso(),
        document: doc,
    };
}

export const emailsApi = {
    list: () => Promise.resolve({ items: [] as EmailSummary[], total: 0 }),
    create: () =>
        Promise.reject(new ApiCallError(400, { code: "UNSUPPORTED", message: "Create from the templates list" })),
    get: (id: string) => raw<DocumentResponse>("GET", `/api/v1/emails/templates/${id}/document`).then(toDetail),
    save: (id: string, document: EmailDocument, baseVersion: number) =>
        raw<unknown>("POST", `/api/v1/emails/templates/${id}/document`, {
            document_json: JSON.stringify(document),
        }).then(() => ({ id, version: baseVersion + 1 })),
    autosave: (id: string, document: EmailDocument, baseVersion: number) =>
        raw<unknown>("POST", `/api/v1/emails/templates/${id}/document`, {
            document_json: JSON.stringify(document),
        }).then(() => ({ id, version: baseVersion + 1 })),
    delete: () => Promise.resolve({ deleted: true }),
    duplicate: (id: string) => Promise.resolve({ id, version: 1 }),
    render: (document: EmailDocument, mode: "production" | "preview" = "preview") =>
        raw<RenderResult>("POST", "/api/v1/emails/render", {
            document_json: JSON.stringify(document),
            mode,
        }),
    versions: (id: string) =>
        raw<{ versions: { index: number; ts: string; engine: string }[] }>(
            "GET",
            `/api/v1/emails/templates/${id}/versions`,
        ).then((r) =>
            (r.versions ?? []).map((v) => ({
                version: v.index,
                kind: v.engine ?? "manual",
                label: null as string | null,
                createdAt: v.ts,
            })),
        ),
    restore: (id: string, version: number) =>
        raw<unknown>("POST", `/api/v1/emails/templates/${id}/versions/${version}/restore`).then(() => ({
            id,
            version,
        })),
};
